import { useCallback, useRef, useState } from "react";
import { createEmptyGrid, type PixelGrid, type ToolId } from "../shared/game";
import { floodFill, paintSquare, setPixel } from "../lib/pixel-grid";

type PixelEditorState = {
  grid: PixelGrid;
  setGrid: (grid: PixelGrid) => void;
  color: string;
  setColor: (color: string) => void;
  tool: ToolId;
  setTool: (tool: ToolId) => void;
  brushSize: number;
  setBrushSize: (size: number) => void;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  clear: () => void;
  beginStroke: () => void;
  applyAt: (row: number, col: number) => void;
  commitStroke: () => void;
};

export function usePixelEditor(): PixelEditorState {
  const [grid, setGridState] = useState<PixelGrid>(createEmptyGrid);
  const [color, setColor] = useState("#e94560");
  const [tool, setTool] = useState<ToolId>("draw");
  const [brushSize, setBrushSize] = useState(1);
  const [history, setHistory] = useState<PixelGrid[]>([createEmptyGrid()]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const strokeStartedRef = useRef(false);

  const setGrid = useCallback((nextGrid: PixelGrid) => {
    setGridState(nextGrid);
    setHistory([nextGrid.map((row) => [...row])]);
    setHistoryIndex(0);
  }, []);

  const pushHistory = useCallback((nextGrid: PixelGrid) => {
    setHistory((previous) => {
      const trimmed = previous.slice(0, historyIndex + 1);
      return [...trimmed, nextGrid.map((row) => [...row])];
    });
    setHistoryIndex((value) => value + 1);
  }, [historyIndex]);

  const applyAt = useCallback((row: number, col: number) => {
    setGridState((current) => {
      if (tool === "draw") {
        return setPixel(current, row, col, color);
      }

      if (tool === "ink") {
        return paintSquare(current, row, col, brushSize, color);
      }

      if (tool === "erase") {
        return paintSquare(current, row, col, brushSize, null);
      }

      return floodFill(current, row, col, current[row][col], color);
    });
  }, [brushSize, color, tool]);

  const beginStroke = useCallback(() => {
    strokeStartedRef.current = true;
  }, []);

  const commitStroke = useCallback(() => {
    if (!strokeStartedRef.current) {
      return;
    }

    strokeStartedRef.current = false;
    setGridState((current) => {
      pushHistory(current);
      return current;
    });
  }, [pushHistory]);

  const undo = useCallback(() => {
    if (historyIndex <= 0) {
      return;
    }

    const nextIndex = historyIndex - 1;
    setHistoryIndex(nextIndex);
    setGridState(history[nextIndex].map((row) => [...row]));
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) {
      return;
    }

    const nextIndex = historyIndex + 1;
    setHistoryIndex(nextIndex);
    setGridState(history[nextIndex].map((row) => [...row]));
  }, [history, historyIndex]);

  const clear = useCallback(() => {
    const emptyGrid = createEmptyGrid();
    setGridState(emptyGrid);
    setHistory([emptyGrid]);
    setHistoryIndex(0);
  }, []);

  return {
    grid,
    setGrid,
    color,
    setColor,
    tool,
    setTool,
    brushSize,
    setBrushSize,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    undo,
    redo,
    clear,
    beginStroke,
    applyAt,
    commitStroke,
  };
}
